import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteAction } from '../../remote-action';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { ServiceRemoteActionBuilder } from '../service-remote-action-builder';
import { DynForm } from 'src/app/_model/dyn-form';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { GpsRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/gps-remote-action-builder-service-step/gps-remote-action-builder-service-step';
import { LocationService } from 'src/app/_services/location/location.service';
import { PlaceTypeRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/placetype-remote-action-builder-service-step/placetype-remote-action-builder-service-step';
import { IMSObject, IMSId } from 'src/app/_model/ims-object';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { Servicejob } from 'src/app/_model/servicejob';
import { DynFormData } from 'src/app/_model/dyn-form-data';
import { TourIdRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/tourid-remote-action-builder-service-step/tourid-remote-action-builder-service-step';
import { StockService } from 'src/app/_services/stock/logic/stock.service';
import {
  CorrectionItem,
  ReturnItem,
  SoldItem,
  StorageInfoItem,
} from 'src/app/_services/stock/logic/stock/stock-logic.service';
import { CargoItem } from 'src/app/_model/cargo-item';
import { TourService } from 'src/app/_services/tour/logic/tour.service';
import { CargoItemsService } from 'src/app/_services/cargo-item/logic/cargo-item.service';
import { TourStopsService } from 'src/app/_services/tour-stop/logic/tour-stop.service';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { OrderItem, OrderItemType } from 'src/app/_model/order-item';
import { OrderItemsService } from 'src/app/_services/order-item/logic/order-item.service';
import { isValueNotSet, isValueSet, LBSUtils } from 'src/app/_util/lbs-utils';
import { AbstractFormFieldXML } from 'src/app/_model/dyn-form/form-fields/abstract-form-field-xml';
import { CargoItemTypesService } from 'src/app/_services/cargo-item-types/logic/cargo-item-types.service';
import { JobSectionIdRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/job-section-id-remote-action-builder-service-step/job-section-id-remote-action-builder-service-step';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { GeneralPostProcessorService } from 'src/app/_services/post-processors/general-post-processor/general-post-processor.service';
import { Pin } from 'src/app/_model/pin';
import { ActionInterpreterService } from 'src/app/_services/action-interpreter/action-interpreter.service';

const getNumberRangeValueSuffix = (form: DynForm, formData: any): string => {
  const rawFields = form.form?.form?.field;
  const fieldsArray: AbstractFormFieldXML[] = rawFields
    ? Array.isArray(rawFields)
      ? rawFields
      : [rawFields]
    : [];
  const fieldName = fieldsArray.find((f) =>
    LBSUtils.convertStringBool(f.$.isManualNextNumberRangeValue, false),
  )?.$.name;
  if (!fieldName) return '';
  const value = formData?.[fieldName];
  return value ? ' ' + value : '';
};

const remoteActionLabelExtraGenerator = (
  formDataPath: string,
  formData: any
) => {
  if (isValueNotSet(formDataPath)) {
    return '';
  }

  const dataValue = formData[formDataPath];
  if (isValueNotSet(formDataPath)) {
    return '';
  }

  return `<br>${dataValue}`;
};

export class DynFormRemoteActionBuilder extends ServiceRemoteActionBuilder {
  constructor(
    private form: DynForm,
    targetObj: IMSObject,
    private dynFormData: DynFormData,
    private pin: Pin,
    appService: AppService,
    private appParameter: AppParameterService,
    private credentialsService: CredentialsService,
    private clientStateService: ClientStateService,
    private servicejobsService: ServicejobsService,
    locationService: LocationService,
    private stockService: StockService,
    private tourService: TourService,
    private tourStopService: TourStopsService,
    private cargoItemsService: CargoItemsService,
    private branchService: BranchesService,
    private orderItemService: OrderItemsService,
    private cargoItemTypesService: CargoItemTypesService,
    formData: any,
    screenShot: any,
    acId: number,
    acukey: string,
    isEditable: boolean,
    private generalPostProcessorService: GeneralPostProcessorService,
    forceUseManualNextNumberRangeValue: string,
    isDraft: boolean = false,
    draftEmpId?: number,
    draftMcId?: number,
    draftServiceJobId?: number,
    isReadyForSignature?: boolean,
    checkAcVersionConflict?: boolean,
    isCloudAutosave?: boolean,
    private logger?: LoggingService
  ) {
    super(
      appService,
      credentialsService,
      {
        endpoint: 'dynform',
        data: {
          image: screenShot,
          isdraft: isDraft,
          targetdraftownerempid: draftEmpId,
          targetdraftownermcid: draftMcId,
          targetdraftownerservicejobid: draftServiceJobId,
          isreadyforsignature: isReadyForSignature,
          // APP4-948: server stores the upload as a conflict branch if the provided acid is no
          // longer the chain head instead of silently appending (optimistic locking)
          checkacversionconflict: checkAcVersionConflict ? true : undefined,
          // APP4-948: queue dedup key — a newer autosave of the same local form data replaces a
          // still pending one (prevents duplicate AC creation while no acid is known yet)
          pi_dedupkey:
            isCloudAutosave && dynFormData.formDataId != null
              ? 'dynformautosave_' + dynFormData.formDataId
              : undefined,
          // APP4-948: the local row this upload belongs to. The queue reads the chain
          // identity from it right before transmitting (see
          // CachedFifoRemoteActionService.resolveLateBoundAcIdentity): a document only gets
          // its acid/acukey with the answer to its FIRST upload, so everything queued in
          // between - a submit right after an autosave above all - was built without one.
          pi_acformdataid:
            form.isCloudAutosaveActive && dynFormData.formDataId != null
              ? dynFormData.formDataId
              : undefined,
          objId: targetObj.getDynFormRemoteActionId(),
          objRef: targetObj.getDynFormRemoteActionRef(),
          skey:
            targetObj instanceof Servicejob
              ? targetObj.id.imsId == undefined
                ? targetObj.getSecondaryKey()
                : undefined
              : undefined,
          sjobkey:
            targetObj instanceof Servicejob
              ? targetObj.id.imsId == undefined
                ? targetObj.getSecondaryKey()
                : undefined
              : undefined,
          sjobref:
            targetObj instanceof Servicejob
              ? targetObj.id.imsId == undefined
                ? targetObj.id.objRef
                : undefined
              : undefined,
          sobjref:
            targetObj instanceof Servicejob
              ? targetObj.id.imsId == undefined
                ? targetObj.id.objRef
                : undefined
              : undefined,
          pinId: pin && !pin.isLocal() ? pin.getImsId() : undefined,
          secondaryPinId: pin && pin.isLocal() ? pin.getImsId() : undefined,
          acid: acId,
          acukey: acukey,
          forceUseManualNextNumberRangeValue: forceUseManualNextNumberRangeValue,
          type: 'FORM',
          options: {
            rawData: {
              formId: form.id.imsId,
              formName: form.name,
              isformfinished: true, // If it is getting submitted it must be finished
              referenceTs: dynFormData.referenceTs,
              formCreatedTs: dynFormData.entityCreatedTstamp,
              // jobId -> Really necessary? Job Id is already TWICE in the request. this would be the third time
              jobId: clientStateService.theState.getValue().serviceJobId
                ? clientStateService.theState.getValue().serviceJobId.imsId
                : undefined,
              formdata: formData,
              pinId: pin && !pin.isLocal() ? pin.getImsId() : undefined,
              secondaryPinId: pin && pin.isLocal() ? pin.getImsId() : undefined,
              _formpi_abortdistribution: dynFormData._formpi_abortdistribution,
              _formpi_attachedextfileids: dynFormData._formpi_attachedextfileids,
              _formpi_requiredextfileextfileids: dynFormData._formpi_requiredextfileextfileids
            },
          },
          // Needed for linking in day protocol
          dynformid: form.id.imsId,
        },
        cachable: !LBSUtils.convertStringBool(
          form.form.form.$.notCachable,
          false
        ),
        fifo: !LBSUtils.convertStringBool(form.form.form.$.notCachable, false),
        authenticationRequired: true,
        shortLabel:
          form.label +
          getNumberRangeValueSuffix(form, formData) +
          remoteActionLabelExtraGenerator(
            form.form.form.$.shortLabelExtraFormDataPath,
            formData
          ),
        shortLabelTranslate: false,
        longLabel:
          (targetObj.displayLabel ?? targetObj.label) +
          remoteActionLabelExtraGenerator(
            form.form.form.$.longLabelExtraFormDataPath,
            formData
          ),
        longLabelTranslate: false,
        pi_delayFilesDeletion: isEditable,
      },
      [
        new TourIdRemoteActionBuilderServiceStep(clientStateService, logger),
        new PlaceTypeRemoteActionBuilderServiceStep(clientStateService, logger),
        new GpsRemoteActionBuilderServiceStep(locationService, logger),
        new JobSectionIdRemoteActionBuilderServiceStep(
          clientStateService,
          logger,
          dynFormData
        ),
      ],
      async (data: any, remoteAction: RemoteAction) => {
        if (data?.options?.rawData?.formdata == undefined) {
          return;
        }

        if (pin) {
          if (pin.isLocal) {
            data.options.rawData.formdata.pinId = pin.getImsId();
          } else {
            data.options.rawData.formdata.secondaryPinId = pin.getImsId();
          }
        }

        // A draft upload is a pure data snapshot: postprocessors (deficiency tickets,
        // stock movements, orderitems, submitted-syscodes, finalizeday) only run for a
        // REAL submit. That mirrors the backend, which skips its own postprocessors for
        // `isdraft` (MobileClientServiceBean: the whole block hangs in `else if
        // (!isDraft)`) - so anything created here would be a local object the server
        // never heard of.
        //
        // It also has to be this way because their counterpart is off for drafts:
        // AbstractFormField.onSubmitted(), which drops the "still to be created" data,
        // is deliberately not called for a draft upload. Running the postprocessors
        // anyway meant every "In Postkorb ablegen" created the whole list again - four
        // entries deposited four times ended up as ten deficiency tickets.
        if (isCloudAutosave || isDraft) {
          return;
        }

        let formData = data.options?.rawData?.formdata;

        this.logger.log(
          this.constructor.name,
          'Handling PostProcessors for the form:',
          form,
          ' formData:',
          formData,
          'objRef:',
          targetObj?.id?.objRef
        );
        const updateObjects =
          await this.generalPostProcessorService.handlePostProcessors(
            form,
            formData,
            targetObj,
            remoteAction
          );

        console.log(
          this.constructor.name,
          'Found update Objects',
          updateObjects
        );
        this.updateRemoteActioFormDataFields(updateObjects);

        this.logger.log(
          this.constructor.name,
          'Executing post generate functions',
          'drivingsales=',
          formData.formpi_drivingsales,
          'loadCargoItem=',
          formData.formpi_loadcargoitems,
          'assigncargoitem=',
          formData.formpi_assigncargoitems,
          'orderitemschecklist=',
          formData.formpi_orderitems_checklist,
          'addorderitem=',
          formData.formpi_addorderitem,
          'alternativeorderitems=',
          formData.formpi_alternative_orderitems,
          'stockchecklist=',
          formData.formpi_stock_checklist,
          'unannouncedreturnorderitem=',
          formData.formpi_unannoucedreturnorderitem
        );

        if (formData.formpi_drivingsales) {
          await this.postProcessDrivingSales(formData);
        }
        if (formData.formpi_loadcargoitems) {
          await this.postProcessLoadCargoItem(formData);
        }
        if (formData.formpi_assigncargoitems) {
          await this.postProcessAssignCargoItem(formData);
        }
        if (formData.formpi_orderitems_checklist) {
          await this.postProcessOrderitemChecklist(formData);
        }
        if (formData.formpi_addorderitem) {
          await this.postProcessAddOrderItem(formData);
        }
        if (formData.formpi_alternative_orderitems) {
          await this.postProcessAlternativeOrderItems(formData);
        }
        if (formData.formpi_stock_checklist) {
          await this.postProcessStockChecklist(formData);
        }
        if (formData.formpi_unannoucedreturnorderitem) {
          await this.postProcessUnannouncedReturnOrderItems(formData);
        }
        
        const dynFormTypeSyscode = form.databag?.dynformtypesyscode;
        if (dynFormTypeSyscode && targetObj.id?.objRef === 'ims/ServiceJob') {
          const sjob = targetObj as Servicejob;
          sjob.addSubmittedDynFormSyscode(dynFormTypeSyscode, Date.now());
          await this.servicejobsService.updateServicejob(sjob)
        }
        if (form.finalizeday) {
          await this.clientStateService.stopWorking();
        }
      },
      logger
    );
  }

  public async next(dataValues?: { key: string; value: any }[]): Promise<any> {
    return super.next(dataValues);
  }

  public async generateRemoteAction(
    dataValues: { key: string; value: any }[]
  ): Promise<RemoteAction> {
    let ra = await super.generateRemoteAction(dataValues);
    if (ra.data['objId'] && ra.timestamp) {
      ra.data['id'] =
        ra.data['objId'] +
        this.credentialsService.credentials$.getValue().username +
        ra.timestamp +
        '_' +
        0; // TODO: Hardcoded zero. If the meaning of this value can be pinned down update.
    }

    // Only the formDataId is passed along - everything the app needs to mirror
    // the created AC locally comes from the upload response's `acdto` key (the
    // full AC DTO, same shape as a syncactivityconfirmations entry). The whole
    // response is merged into these params, see CachedFifoRemoteActionService.
    ra.afterSendActions = [
      {
        type: 'action',
        target: 'updateactivityconfirmation',
        params: {
          formDataId: this.dynFormData.formDataId.toString(),
        },
      },
    ];

    return ra;
  }

  private async postProcessDrivingSales(formData) {
    let formpi_drivingsales = formData.formpi_drivingsales;
    let formpi_drivingsales_key = formData.formpi_drivingsales_key;
    let drivingSales = formData[formpi_drivingsales_key];
    this.logger.log(
      this.constructor.name,
      'postProcessDrivingSales',
      'Found driving sales',
      formpi_drivingsales,
      formpi_drivingsales_key,
      drivingSales
    );

    let soldItems: SoldItem[] = [];
    for (const drivingSale of drivingSales) {
      if (drivingSale['drivingsale']) {
        this.logger.log(
          this.constructor.name,
          'postProcessDrivingSales',
          'found a drivingsale with Value',
          drivingSale
        );
        soldItems.push({
          amt: drivingSale['drivingsale'],
          posidx: drivingSale['posidx'],
          tourId: this.clientStateService.theState.getValue().tourId,
          isDrivingSale: true,
        });
      }
    }
    if (soldItems.length > 0) {
      let createdOrderItems = await this.stockService.itemsSold(soldItems);
      this.logger.log(
        this.constructor.name,
        'postProcessDrivingSales',
        'createdOrderItems:',
        createdOrderItems
      );

      let updatedOrderItems = [];
      for (let drivingSale of drivingSales) {
        let orderItem = createdOrderItems.find(
          (coi) => coi.posIdx == drivingSale['posidx']
        );

        if (orderItem) {
          drivingSale['newPosidx'] = orderItem.newPosIdx;
          drivingSale['assignedsjobid'] = orderItem.assignedSjobId;
        }

        updatedOrderItems.push(drivingSale);
      }

      let updateObject = {
        key: formpi_drivingsales_key,
        value: updatedOrderItems,
      };
      this.updateRemoteActionFields([updateObject]);
    }
  }

  private async postProcessLoadCargoItem(formData) {
    let formpi_loadcargoitems = formData.formpi_loadcargoitems;
    let formpi_loadcargoitems_key = formData.formpi_loadcargoitems_key;
    let loadCargoItemTypes = formData[formpi_loadcargoitems_key];
    this.logger.log(
      this.constructor.name,
      'postProcessLoadCargoItem',
      'Found load cargoitems',
      formpi_loadcargoitems,
      formpi_loadcargoitems_key,
      loadCargoItemTypes
    );

    let tourId = this.clientStateService.theState.getValue()?.tourId;
    if (tourId) {
      let tour = this.tourService.getTourById(tourId);

      for (let cargoItemType of loadCargoItemTypes) {
        if (cargoItemType.amtloaded > 0) {
          this.logger.log(
            this.constructor.name,
            'postProcessLoadCargoItem',
            'create cargoitem for',
            cargoItemType
          );
          await this.cargoItemsService.createCargoItemForTour(
            {
              id: undefined,
              secondaryid: undefined,
              extid: cargoItemType.extid,
              branch: undefined,
              itemvalue: cargoItemType.itemvalue,
              label: cargoItemType.label,
              name: cargoItemType.label,
              serialnumber: undefined,
              servicejob: undefined,
              sortkey: undefined,
              tour: {
                id: tourId.imsId,
                label: tour?.label || '',
              },
              amtdlv: 0,
              amtopen: cargoItemType.amtloaded,
              amtreturn: 0,
              cargoitemtype: {
                id: cargoItemType.id,
                label: cargoItemType.label,
              },
            },
            tour
          );
        } else {
          this.logger.log(
            this.constructor.name,
            'postProcessLoadCargoItem',
            'cargoItem has a value of amtloaded 0',
            cargoItemType
          );
        }
      }
    }
  }

  private async postProcessAssignCargoItem(formData) {
    let formpi_assigncargoitems = formData.formpi_assigncargoitems;
    let formpi_assigncargoitems_key = formData.formpi_assigncargoitems_key;
    let assignCargoItems = formData[formpi_assigncargoitems_key];
    this.logger.log(
      this.constructor.name,
      'postProcessAssignCargoItem',
      'Found assign cargoitems',
      formpi_assigncargoitems,
      formpi_assigncargoitems_key,
      assignCargoItems
    );

    let tourId = this.clientStateService.theState.getValue()?.tourId;
    let tour = this.tourService.getTourById(tourId);
    let tourStopId = this.clientStateService.theState.getValue()?.tourStopId;

    this.logger.log(
      this.constructor.name,
      'postProcessAssignCargoItem',
      'try assigning cargoitems',
      assignCargoItems,
      'from generel tour',
      tourId,
      'to tourstop',
      tourStopId
    );
    if (tourId && tourStopId) {
      let tourStop = this.tourStopService.getTourStopById(tourStopId);
      this.logger.log(
        this.constructor.name,
        'postProcessAssignCargoItem',
        'found tourstop',
        tourStop
      );

      const sjobs =
        this.servicejobsService.getServiceJobsForTourStopId(tourStopId);
      const sjob = LBSUtils.getAnyNonReturnTransportTypeSjob(sjobs);

      let branchId: IMSId;
      if (tourStop.branchIds.length > 0) {
        branchId = tourStop.branchIds[0];
      }
      let updatedCargoItems = [];
      if (sjob || branchId) {
        let branch = this.branchService.getBranchById(branchId);
        for (const assignCargoItem of assignCargoItems) {
          if (
            assignCargoItem?.amtassign > 0 ||
            assignCargoItem?.amtreturn > 0
          ) {
            this.logger.log(
              this.constructor.name,
              'postProcessAssignCargoItem',
              'Try assigning cargoItem',
              assignCargoItem,
              'to sjob',
              sjob,
              'and/or branch',
              branch
            );

            this.logger.log(
              this.constructor.name,
              'postProcessAssignCargoItem',
              'id not set',
              assignCargoItem.id,
              isValueNotSet(assignCargoItem.id),
              'extid set',
              assignCargoItem.extid,
              isValueSet(assignCargoItem.extid)
            );
            let cargoItem: CargoItem;
            if (
              isValueNotSet(assignCargoItem.id) &&
              isValueSet(assignCargoItem.extid)
            ) {
              // It is a placeholder CargoItem that is not yet registered on the tour, but needs to be there for returned cargoitems
              let cargoItemType =
                this.cargoItemTypesService.getCargoItemTypeByExtId(
                  assignCargoItem.extid
                );
              cargoItem = CargoItem.fromDto({
                amtdlv: undefined,
                amtopen: 0,
                amtreturn: undefined,
                branch: {
                  id: undefined,
                  label: undefined,
                },
                cargoitemtype: {
                  id: cargoItemType.id.imsId,
                  label: cargoItemType.label,
                },
                extid: cargoItemType.extId,
                itemvalue: cargoItemType.itemValue,
                label: cargoItemType.label,
                name: cargoItemType.label,
                servicejob: {
                  id: undefined,
                  label: undefined,
                },
                id: undefined,
                secondaryid: undefined,
                serialnumber: undefined,
                sortkey: undefined,
                tour: {
                  id: tour.id.imsId,
                  label: tour.label,
                },
              });
              this.logger.log(
                this.constructor.name,
                'postProcessAssignCargoItem',
                'create tour cargoitem',
                cargoItem
              );
              await this.cargoItemsService.assignTourCargoItemtoSjob(
                cargoItem,
                sjob,
                branch,
                assignCargoItem?.amtassign || 0,
                assignCargoItem?.amtreturn || 0
              );
            } else {
              cargoItem = this.cargoItemsService.getCargoItemById({
                imsId: assignCargoItem.id,
                secondaryId: undefined,
                objRef: 'ims/CargoItem',
              });

              if (cargoItem == undefined) {
                this.logger.log(
                  this.constructor.name,
                  'postProcessAssignCargoItem',
                  'Couldnt find cargoitem with imsid',
                  assignCargoItem.id,
                  'try looking for one with secondary id'
                );
                cargoItem = this.cargoItemsService.getCargoItemById({
                  imsId: undefined,
                  secondaryId: assignCargoItem.id,
                  objRef: 'ims/CargoItem',
                });
              }

              this.logger.log(
                this.constructor.name,
                'postProcessAssignCargoItem',
                'found cargoitem',
                cargoItem
              );
              if (cargoItem) {
                await this.cargoItemsService.assignTourCargoItemtoSjob(
                  cargoItem,
                  sjob,
                  branch,
                  assignCargoItem?.amtassign || 0,
                  assignCargoItem?.amtreturn || 0
                );
              }
            }
          } else {
            this.logger.log(
              this.constructor.name,
              'postProcessAssignCargoItem',
              'amtassign is not set or 0',
              assignCargoItem
            );
          }

          assignCargoItem['assignedsjobid'] = sjob.getImsId();
          updatedCargoItems.push(assignCargoItem);
        }

        let newCargoItems = this.cargoItemsService.cargoItems$.getValue();
        this.logger.log(
          this.constructor.name,
          'CargoItems after assigning',
          newCargoItems
        );
      }
      let updateObject = {
        key: formpi_assigncargoitems_key,
        value: updatedCargoItems,
      };
      this.updateRemoteActionFields([updateObject]);
    }
  }

  private async postProcessOrderitemChecklist(formData) {
    this.logger.log(
      this.constructor.name,
      'postProcessOrderitemChecklist',
      'Process commissioned orderitems'
    );
    let formpi_orderitems_checklist = formData.formpi_orderitems_checklist;
    let formpi_orderitems_checklist_key =
      formData.formpi_orderitems_checklist_key;
    let commissioned_orderitems_per_sjob =
      formData[formpi_orderitems_checklist_key];
    this.logger.log(
      this.constructor.name,
      'postProcessOrderitemChecklist',
      'Found commissioned orderitems per sjob',
      formpi_orderitems_checklist,
      formpi_orderitems_checklist_key,
      commissioned_orderitems_per_sjob
    );

    let soldItems: SoldItem[] = [];
    for (const commissioned_orderitem_sjob of commissioned_orderitems_per_sjob) {
      if (commissioned_orderitem_sjob['orderitems']) {
        let commissioned_orderitems = commissioned_orderitem_sjob['orderitems'];
        this.logger.log(
          this.constructor.name,
          'postProcessOrderitemChecklist',
          'Found commissioned orderitems',
          commissioned_orderitems
        );
        for (const commissioned_orderitem of commissioned_orderitems) {
          if (commissioned_orderitem['amtdlv'] != undefined) {
            this.logger.log(
              this.constructor.name,
              'postProcessOrderitemChecklist',
              'Reduce stock of material',
              commissioned_orderitem['extid'],
              'by amount',
              commissioned_orderitem['amtdlv']
            );
            soldItems.push({
              amt: commissioned_orderitem['amtdlv'],
              posidx: commissioned_orderitem['posidx'],
              tourId: this.clientStateService.theState.getValue().tourId,
            });
          }
        }
      }
    }
    if (soldItems.length > 0) {
      let createdOrderItems = await this.stockService.itemsSold(soldItems);
      this.logger.log(
        this.constructor.name,
        'postProcessOrderitemChecklist',
        'createdOrderItems:',
        createdOrderItems
      );

      let updatedOrderItems = [];
      for (let commissioned_orderitem_sjob of commissioned_orderitems_per_sjob) {
        let orderItem = createdOrderItems.find(
          (coi) => coi.posIdx == commissioned_orderitem_sjob['posidx']
        );

        if (orderItem) {
          commissioned_orderitem_sjob['newPosidx'] = orderItem.newPosIdx;
        }

        updatedOrderItems.push(commissioned_orderitem_sjob);
      }

      let updateObject = {
        key: formpi_orderitems_checklist_key,
        value: updatedOrderItems,
      };
      this.updateRemoteActionFields([updateObject]);
    }
  }

  private async postProcessAddOrderItem(formData) {
    this.logger.log(
      this.constructor.name,
      'postProcessAddOrderItem',
      'Process add orderitems'
    );
    let formpi_addorderitem = formData.formpi_addorderitem;
    let formpi_addorderitem_key: string = formData.formpi_addorderitem_key;
    let formpi_addorderitem_isalternative =
      formData.formpi_addorderitem_isalternative;
    let addedOrderItems = formData[formpi_addorderitem_key];
    this.logger.log(
      this.constructor.name,
      'postProcessAddOrderItem',
      'Found added orderitems',
      addedOrderItems,
      formpi_addorderitem,
      formpi_addorderitem_key,
      formpi_addorderitem_isalternative
    );

    let tourId = this.clientStateService.theState.getValue()?.tourId;
    if (tourId == undefined) {
      this.logger.log(
        this.constructor.name,
        'postProcessAddOrderItem',
        'no tour id available. abort'
      );
    }
    let tour = this.tourService.getTourById(tourId);
    if (tour == undefined) {
      this.logger.log(
        this.constructor.name,
        'postProcessAddOrderItem',
        'no tour available. abort'
      );
    }

    let updatedOrderItems = [];
    for (const addOrderItem of addedOrderItems) {
      this.logger.log(
        this.constructor.name,
        'postProcessAddOrderItem',
        'add orderitem to stock',
        addOrderItem
      );
      let orderItem = OrderItem.fromDto(
        {
          id: undefined,
          secondaryid: undefined,
          label: addOrderItem.material,
          localStorageInfo: addOrderItem.localStorageInfo,
        },
        {},
        {
          material: {
            id: addOrderItem.matid,
            label: addOrderItem.material,
          },
          tour: {
            id: tour.id.imsId,
            label: tour.label,
          },
          amtord: addOrderItem.amtord,
          posvalue: addOrderItem.price,
          isalternativearticle: formpi_addorderitem_isalternative,
          isdrivingsale: !formpi_addorderitem_isalternative,
          localStorageInfo: addOrderItem.localStorageInfo,
        },
        this.appParameter.getFormatLabelForObject('ims/OrderItem')
      );

      let createdOrderItem =
        await this.orderItemService.updateOrCreateOrderItem(orderItem);

      let updatedAddOrderItem = addOrderItem;
      updatedAddOrderItem['posidx'] = createdOrderItem.posidx;
      updatedOrderItems.push(updatedAddOrderItem);
    }
    let updateObject = {
      key: formpi_addorderitem_key,
      value: updatedOrderItems,
    };
    this.updateRemoteActionFields([updateObject]);
  }

  private async postProcessAlternativeOrderItems(formData) {
    let formpi_alternative_orderitems = formData.formpi_alternative_orderitems;
    let formpi_alternative_orderitems_key =
      formData.formpi_alternative_orderitems_key;
    let alternativeOrderitems = formData[formpi_alternative_orderitems_key];
    this.logger.log(
      this.constructor.name,
      'postProcessAlternativeOrderItems',
      'Found alternative order items',
      formpi_alternative_orderitems,
      formpi_alternative_orderitems_key,
      alternativeOrderitems
    );

    let soldItems: SoldItem[] = [];
    for (const alternativeOrderItem of alternativeOrderitems) {
      if (alternativeOrderItem['amtdlv']) {
        this.logger.log(
          this.constructor.name,
          'postProcessAlternativeOrderItems',
          'found a alternativeOrderItem with Value',
          alternativeOrderItem
        );
        soldItems.push({
          amt: alternativeOrderItem['amtdlv'],
          posidx: alternativeOrderItem['posidx'],
          tourId: this.clientStateService.theState.getValue().tourId,
        });
      }
    }
    this.logger.log(
      this.constructor.name,
      'postProcessAlternativeOrderItems',
      'soldItems found',
      soldItems.length
    );
    if (soldItems.length > 0) {
      this.logger.log(
        this.constructor.name,
        'postProcessAlternativeOrderItems',
        'call itemsSold with',
        soldItems
      );
      let createdOrderItems = await this.stockService.itemsSold(soldItems);
      this.logger.log(
        this.constructor.name,
        'postProcessAlternativeOrderItems',
        'createdOrderItems:',
        createdOrderItems
      );

      let updatedOrderItems = [];
      for (let alternativeOrderItem of alternativeOrderitems) {
        let orderItem = createdOrderItems.find(
          (coi) => coi.posIdx == alternativeOrderItem['posidx']
        );

        if (orderItem) {
          alternativeOrderItem['newPosidx'] = orderItem.newPosIdx;
          alternativeOrderItem['assignedsjobid'] = orderItem.assignedSjobId;
        }

        updatedOrderItems.push(alternativeOrderItem);
      }

      let updateObject = {
        key: formpi_alternative_orderitems_key,
        value: updatedOrderItems,
      };
      this.updateRemoteActionFields([updateObject]);
    }
  }

  private async postProcessStockChecklist(formData) {
    let formpi_stock_checklist = formData.formpi_stock_checklist;
    let formpi_stock_checklist_key = formData.formpi_stock_checklist_key;
    let stockchecklist = formData[formpi_stock_checklist_key];
    this.logger.log(
      this.constructor.name,
      'postProcessStockChecklist',
      'Found stockchecklist',
      formpi_stock_checklist,
      formpi_stock_checklist_key,
      stockchecklist
    );

    let correctionItems: CorrectionItem[] = [];
    let storageInfoItems: StorageInfoItem[] = [];
    for (const stockGroup of stockchecklist) {
      for (const stockItem of stockGroup.items) {
        if (stockItem['correction'] != undefined) {
          let correction = stockItem['correction'];
          this.logger.log(
            this.constructor.name,
            'postProcessStockCheckliste correct',
            stockItem['extid'],
            stockItem['label'],
            'by',
            correction
          );
          correctionItems.push({
            amt: correction,
            matId: stockItem['matid'],
          });
        }
        if (stockItem['localStorageInfo'] != undefined) {
          let localStorageInfo = stockItem['localStorageInfo'];
          this.logger.log(
            this.constructor.name,
            'postProcessStockCheckliste set storage info of',
            stockItem['extid'],
            stockItem['label'],
            'to',
            localStorageInfo
          );
          storageInfoItems.push({
            info: localStorageInfo,
            matId: stockItem['matid'],
          });
        }
      }
    }
    if (correctionItems.length > 0) {
      await this.stockService.correctMaterial(correctionItems);
    }
    if (storageInfoItems.length > 0) {
      await this.orderItemService.updateOrderItemStorageInfos(storageInfoItems);
    }
  }

  private async postProcessUnannouncedReturnOrderItems(formData) {
    let formpi_unannoucedreturnorderitem =
      formData.formpi_unannoucedreturnorderitem;
    let formpi_unannoucedreturnorderitem_key =
      formData.formpi_unannoucedreturnorderitem_key;
    const formpi_unannoucedreturnorderitem_type = formData.formpi_unannoucedreturnorderitem_type as OrderItemType
    let returnOrderItems: any[] =
      formData[formpi_unannoucedreturnorderitem_key];
    this.logger.log(
      this.constructor.name,
      'postProcessUnannouncedReturnOrderItems',
      'Found return orderitems',
      formpi_unannoucedreturnorderitem,
      formpi_unannoucedreturnorderitem_key,
      formpi_unannoucedreturnorderitem_type,
      returnOrderItems
    );

    let returnItems: ReturnItem[] = [];
    for (const { index, value } of returnOrderItems.map((value, index) => ({
      index,
      value,
    }))) {
      this.logger.log(
        this.constructor.name,
        'parse orderitem',
        value,
        'with index',
        index
      );
      let rowidx = index;
      let comment = value['comment'];
      let amtdlv = value['amtdlv'];
      let unit = value['unit'];
      let matId = value['matid'];

      returnItems.push({
        rowidx,
        amtdlv,
        comment,
        unit,
        matId: matId,
        type: formpi_unannoucedreturnorderitem_type,
      });
    }

    if (returnItems.length > 0) {
      this.logger.log(
        this.constructor.name,
        'postProcessUnannouncedReturnOrderItems',
        'add',
        returnItems.length,
        'returnitems to stock'
      );
      let createdOrderItems = await this.stockService.addUnannouncedReturnItems(
        returnItems
      );

      this.logger.log(
        this.constructor.name,
        'postProcessUnannouncedReturnOrderItems',
        'createdOrderItems:',
        createdOrderItems
      );

      let updatedOrderItems = [];
      for (const { index, value } of returnOrderItems.map((value, index) => ({
        index,
        value,
      }))) {
        let orderItem = createdOrderItems.find((coi) => coi.rowidx == index);

        if (orderItem) {
          value['newPosidx'] = orderItem.newPosIdx;
          value['assignedsjobid'] = orderItem.assignedSjobId;
        }

        updatedOrderItems.push(value);
      }

      let updateObject = {
        key: formpi_unannoucedreturnorderitem_key,
        value: updatedOrderItems,
      };
      this.updateRemoteActionFields([updateObject]);
    }
  }
}
